// The Kulki board (.ai/game-rules.md "Kulki"): the 9 x 9 grid as big as the room between the bars allows (a square), the
// next 3 balls and the best score over it. Tap a ball to pick it (it bounces; the cells it can reach light up a little),
// tap an empty cell to send it there; a cell it cannot reach shakes. The ball runs its path a cell a step, a line shrinks
// away with its points over it, the new balls grow in. The balls are pictures in the card deck's style (balls.ts). The
// end of the game (a full board) opens the end window.
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n';
import { useUiState, type WindowInstance } from '../store/uiStore';
import { useController } from '../store/gameStore';
import { isShortGame, reloadForNewGame } from '../hearts/nextGame';
import { AdSlot } from '../windows/AdSlot';
import { adsEnabled, WindowFrame } from '../windows/WindowFrame';
import { WinBackdrop } from '../windows/WinWindow';
import { useFitWindow } from '../windows/useFitWindow';
import { useMultiplayerState } from '../multiplayer/store';
import { CELLS, SIZE } from './rules';
import { kulkiStore, reachableFrom, useKulki } from './store';
import { ballSetOf, ballSrc } from './balls';
import '../hearts/hearts.css';
import './kulki.css';

/** Room kept round the board, for the bar of the next balls over it, and for that bar beside it on a low screen (px). */
const PAD = 12;
const BAR = 52;
const BAR_BESIDE = 108;
const MAX_SIDE = 720;

/** The board's side: the largest square that fits the area, with the bar over it or, when that gives a bigger board (a
 *  low landscape screen), beside it. */
/** A race's opponent line: under 1150 px wide it is a pill just over the dock, over the board's last row (app.css
 *  #footer_multiplayer), so the board keeps this much free under it. */
const RACE_LINE = 34;

/** How far down the free height the board sits: a quarter, not the middle (owner 2026-11-03 on a big screen: "może bym
 *  dał planszę trochę wyżej"). */
const FREE_ABOVE = 0.25;

function useSide(ref: React.RefObject<HTMLDivElement | null>, racing: boolean): { side: number; beside: boolean; top: number } {
  const [fit, setFit] = useState({ side: 0, beside: false, top: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fit = () => {
      const below = racing && innerWidth < 1150 ? RACE_LINE : 0;
      const w = el.clientWidth - 2 * PAD;
      const h = el.clientHeight - 2 * PAD - below;
      // the bar goes beside the board only when that makes the board bigger (up to its largest size, 720 px)
      const over = Math.min(w, h - BAR, MAX_SIDE);
      const beside = Math.min(w - BAR_BESIDE, h, MAX_SIDE);
      // a whole number of pixels a cell: the grid lines stay sharp
      const side = Math.max(SIZE * 16, Math.floor(Math.max(over, beside) / SIZE) * SIZE);
      const tall = beside > over ? side : side + BAR;
      setFit({ side, beside: beside > over, top: PAD + Math.max(0, Math.round((h - tall) * FREE_ABOVE)) });
    };
    fit();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(fit) : null;
    ro?.observe(el);
    addEventListener('resize', fit);
    return () => {
      ro?.disconnect();
      removeEventListener('resize', fit);
    };
  }, [ref, racing]);
  return fit;
}

function Ball({ set, colour, className }: { set: string; colour: number; className?: string | undefined }) {
  return <img className={`kk-ball${className ? ` ${className}` : ''}`} src={ballSrc(set, colour)} alt="" draggable={false} data-colour={colour} />;
}

export function KulkiTable() {
  const st = useKulki();
  const skin = useUiState().options.skin;
  const deck = skin.deckType;
  const set = ballSetOf(skin.deckType, skin.ballType);
  const ref = useRef<HTMLDivElement>(null);
  const racing = useMultiplayerState().gameStarted;
  const { side, beside, top } = useSide(ref, racing);
  const g = st.game;
  const reach = reachableFrom(st);
  const leaving = new Set(st.leaving);
  const arriving = new Set(st.arriving);
  const tap = (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const cell = (e.target as Element).closest<HTMLElement>('[data-cell]');
    if (cell) kulkiStore().tap(Number(cell.dataset.cell));
  };
  const cell = side / SIZE;
  return (
    <div id="main" ref={ref} className="board kk" data-board="" data-game="kulki" data-deck={deck} data-busy={st.busy || undefined}>
      {g && side ? (
        <div className="kk-wrap" data-beside={beside || undefined} style={{ '--kk-side': `${side}px`, '--kk-cell': `${cell}px`, top: `${top}px` } as CSSProperties}>
          <div className="kk-bar">
            <div className="kk-next" aria-label={t('kulki.next')}>
              <span className="kk-bar__label">{t('kulki.next')}</span>
              {g.over ? null : g.next.map((c, i) => <Ball key={`${g.moves}-${i}`} set={set} colour={c} className="kk-ball--next" />)}
            </div>
            <div className="kk-best">
              <span className="kk-bar__label">{t('kulki.best')}</span>
              <b>{Math.max(st.best, g.over ? g.score : 0)}</b>
            </div>
          </div>
          <div className="kk-grid" role="grid" onPointerDown={tap}>
            {Array.from({ length: CELLS }, (_, i) => {
              const c = st.moving?.at === i ? st.moving.colour : st.cells[i]!;
              return (
                <div
                  // a new key restarts the shake of a cell the ball cannot reach
                  key={st.blocked?.at === i ? `${i}-${st.blocked.n}` : i}
                  className="kk-cell"
                  data-cell={i}
                  data-reach={reach.has(i) || undefined}
                  data-picked={st.selected === i || undefined}
                  data-blocked={st.blocked?.at === i ? st.blocked.n : undefined}
                >
                  {c ? (
                    <Ball
                      set={set}
                      colour={c}
                      className={st.selected === i ? 'kk-ball--picked' : leaving.has(i) ? 'kk-ball--leave' : arriving.has(i) ? 'kk-ball--grow' : st.moving?.at === i ? 'kk-ball--run' : undefined}
                    />
                  ) : null}
                </div>
              );
            })}
            {st.gain ? (
              <div
                key={st.gain.n}
                className="kk-gain"
                style={{ left: `${((st.gain.at % SIZE) + 0.5) * cell}px`, top: `${(Math.floor(st.gain.at / SIZE) + 0.5) * cell}px` }}
              >
                +{st.gain.points}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** New game after the end: a full reload, as the solitaires' Win window does (the ads refresh); after a game under 2
 *  minutes the next one at once (hearts/nextGame.ts). */
function useNewGame(): () => void {
  const ctrl = useController();
  const { startedAt, endedAt } = useKulki();
  return () => (isShortGame(startedAt, endedAt) ? void ctrl.newGame() : reloadForNewGame('kulki'));
}

/** How many steps the window may shrink its parts to fit a low screen (windows/useFitWindow.ts). */
const FIT_STEPS = 9; // as WinWindow (win.css data-fit-1..9)

/** The balls of the hero: a fan of five over the medal, as the cards in the solitaires' Win window. */
const FAN = [1, 2, 3, 4, 5];
/** The confetti on a new best score: the player's balls (as WinWindow's cards), laid out from the index. */
const CONFETTI_MS = 4200;
const CONFETTI_PIECES = 110;

function BallConfetti({ set }: { set: string }) {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const id = window.setTimeout(() => setOn(false), CONFETTI_MS);
    return () => window.clearTimeout(id);
  }, []);
  if (!on) return null;
  const frac = (n: number) => n - Math.floor(n);
  return createPortal(
    <div className="win_confetti kk-confetti" aria-hidden="true">
      {Array.from({ length: CONFETTI_PIECES }, (_, i) => {
        const a = frac(Math.sin(i * 12.9898) * 43758.5453);
        const b = frac(Math.sin(i * 78.233) * 12543.1234);
        const c = frac(Math.sin(i * 39.425) * 24634.6345);
        const style = {
          '--x': `${(i / CONFETTI_PIECES) * 100 + (a - 0.5) * 4}vw`,
          '--dx': `${(b - 0.5) * 220}px`,
          '--r': `${(c - 0.5) * 900}deg`,
          '--ry': '0deg',
          '--s': `${0.7 + b * 0.6}`,
          '--d': `${Math.round(c * 2100)}ms`,
          '--t': `${1400 + Math.round(a * 600)}ms`,
          backgroundImage: `url("${ballSrc(set, 1 + (i % 7))}")`,
        } as CSSProperties;
        return <i key={i} style={style} />;
      })}
    </div>,
    document.body,
  );
}

/** The score counts up from 0 (about a second), as the Win window's numbers come one after another. */
function useCountUp(to: number): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!to) return setN(0);
    const start = performance.now();
    const ms = Math.min(1400, 500 + to);
    let raf = 0;
    const tick = (now: number) => {
      const k = Math.min(1, (now - start) / ms);
      setN(Math.round(to * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return n;
}

/** The end of the game (owner 2026-11-03: "zrób ładniejszy ekran wygranej"): the solitaires' Win window (win.css: the
 *  golden rays, the fan with the spinning medal, the tickets and the red score) with balls for the cards. A new best
 *  score says so and rains balls; otherwise the line says how far the best score was. */
export function KulkiEndWindow({ instance }: { instance: WindowInstance }) {
  const st = useKulki();
  const g = st.game;
  const skin = useUiState().options.skin;
  const ctrl = useController();
  useFitWindow('.lbx_window.lbx_kulki_end', FIT_STEPS, g ? `${g.score}` : null);
  const newGame = useNewGame();
  const score = useCountUp(g?.score ?? 0);
  if (!g) return null;
  const record = g.score > st.best;
  const best = Math.max(st.best, g.score);
  const sub = record ? t('win.newBest') : st.best > g.score ? t('kulki.end.toBest', { n: st.best - g.score }) : t('kulki.end.again');
  const mins = Math.floor(st.seconds / 60);
  return (
    <WindowFrame name="kulki_end" header={t('kulki.end.title')} overlay closing={instance.closing} className={`lbx_win${adsEnabled() ? '' : ' no-ads'}`}>
      <div className="tab_contents">
        <WinBackdrop back={skin.cardBackType} />
        {record && !st.demo ? <BallConfetti set={ballSetOf(skin.deckType, skin.ballType)} /> : null}
        <div className="win_cont" data-record={record || undefined}>
          <div className="win_hero">
            <div className="win_trophy" aria-hidden="true">
              <div className="win_rays" />
              {/* the balls hang round the medal and take their size from it: win.css makes the medal smaller on a phone
                  or a low screen, and the arc with it (owner 2026-11-03, iPhone: "góra przycięta") */}
              <div className="win_medal">
                <div className="kk-fan">
                  {FAN.map((c, i) => (
                    <img key={c} className="kk-fan__ball" src={ballSrc(ballSetOf(skin.deckType, skin.ballType), c)} alt="" draggable={false} style={{ '--i': i - 2 } as CSSProperties} />
                  ))}
                </div>
                <div className="win_medal_spin">
                  {Array.from({ length: 8 }, (_, i) => (
                    <div key={i} className="win_medal_edge" style={{ transform: `translateZ(${-i - 1}px)` }} />
                  ))}
                  <div className="win_medal_face">★</div>
                  <div className="win_medal_face win_medal_back">●</div>
                </div>
              </div>
            </div>
            <div className="win_congrats">{record ? t('win.congrats') : t('kulki.end.full')}</div>
            <div className="win_subline">{sub}</div>
          </div>
          <table>
            <tbody>
              <tr>
                <td className="name td_game_type">{t('winGameTypeHead')}</td>
                {/* the version as a number, like "Klondike 3": the whole words were cut on a low screen */}
                <td className="value td_game_type" title={t(`kulki.line.${g.line}`)}>
                  {t('win.kulki')} · {g.line}
                </td>
              </tr>
              <tr>
                <td className="name td_score_type">{t('kulki.end.lines')}</td>
                <td className="value td_score_type">{g.lines}</td>
              </tr>
              <tr>
                <td className="name td_moves win_active win_done">{t('winMovesHead')}</td>
                <td id="kulki_moves" className="value td_moves win_active win_done">
                  {g.moves}
                </td>
              </tr>
              <tr>
                <td className="name td_time win_active win_done">{t('winTimeHead')}</td>
                <td id="kulki_time" className="value td_time win_active win_done">
                  {mins}:{String(st.seconds % 60).padStart(2, '0')}
                </td>
              </tr>
              <tr>
                <td className="name td_bonus win_active win_done">{t('kulki.end.longest')}</td>
                <td id="kulki_longest" className="value td_bonus win_active win_done">
                  {g.longest || '-'}
                </td>
              </tr>
              <tr>
                <td className={`name td_score win_active${score === g.score ? ' win_done' : ''}`}>{t('winScoresHead')}</td>
                <td id="kulki_score" className={`value td_score win_active${score === g.score ? ' win_done' : ''}`}>
                  {score}
                </td>
              </tr>
              <tr>
                <td className="name td_best_score win_active win_done">{t('winBestScoresHead')}</td>
                <td id="kulki_best" className="value td_best_score win_active win_done">
                  {best}
                </td>
              </tr>
            </tbody>
          </table>
          <AdSlot slot={2} />
        </div>
        <div className="win_buttons">
          <input className="new_game" id="kulki_new" type="button" value={t('winNewGameButton')} onClick={newGame} />
          <input className="redeal_game" id="kulki_retry" type="button" value={t('winRedealButton')} onClick={() => ctrl.retryGame()} />
        </div>
      </div>
    </WindowFrame>
  );
}
